import React, { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import RichTextEditor from "./RichTextEditor";
import PlaceholderBar from "./PlaceholderBar";
import usePlaceholderInsert from "./usePlaceholderInsert";
import {
  createTemplate,
  updateTemplate,
  fetchEmailContractors,
  getApiError,
} from "./emailApi";
import {
  TEMPLATE_SCOPES,
  findUnknownPlaceholders,
  getTemplateScope,
  isBodyEmpty,
  toToken,
} from "./emailUtils";

const getContractorLabel = (contractor) => {
  const name = contractor.name || `Contractor ID: ${contractor.id}`;
  return contractor.companyName && contractor.companyName !== name
    ? `${name} — ${contractor.companyName}`
    : name;
};

const TemplateEditorDialog = ({
  open,
  template,
  currentSession,
  onClose,
  onSaved,
}) => {
  const isEdit = Boolean(template);
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [scope, setScope] = useState(TEMPLATE_SCOPES.GLOBAL);
  const [contractor, setContractor] = useState(null);
  const [contractors, setContractors] = useState([]);
  const [contractorSearch, setContractorSearch] = useState("");
  const [contractorsLoading, setContractorsLoading] = useState(false);
  const [contractorsError, setContractorsError] = useState("");
  const [contractorsRetry, setContractorsRetry] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const { subjectRef, handleEditorReady, insertPlaceholder } =
    usePlaceholderInsert(subject, setSubject);

  useEffect(() => {
    if (open) {
      setName(template?.name || "");
      setSubject(template?.subject || "");
      setBody(template?.body || "");
      setScope(getTemplateScope(template));
      const contractorId = String(template?.contractorId ?? "").trim();
      // Keep an existing association even when it is outside the search results.
      // Templates may return contractorName/companyName for display; ID is authoritative.
      setContractor(
        contractorId
          ? {
              id: contractorId,
              name: template?.contractorName || "",
              companyName: template?.companyName || "",
            }
          : null,
      );
      setContractors([]);
      setContractorSearch("");
      setContractorsError("");
      setContractorsRetry(0);
      setError("");
    }
  }, [open, template]);

  useEffect(() => {
    if (!open || scope !== TEMPLATE_SCOPES.CONTRACTOR) return;

    const controller = new AbortController();
    let active = true;
    setContractorsLoading(true);
    setContractorsError("");
    setContractors([]);

    const timer = setTimeout(async () => {
      try {
        const results = await fetchEmailContractors(currentSession, {
          search: contractorSearch,
          signal: controller.signal,
        });
        if (active) setContractors(results);
      } catch (e) {
        if (active) {
          setContractorsError(getApiError(e, "Failed to load contractors."));
        }
      } finally {
        if (active) setContractorsLoading(false);
      }
    }, 300);

    return () => {
      active = false;
      clearTimeout(timer);
      controller.abort();
    };
  }, [
    open,
    template,
    scope,
    currentSession,
    contractorSearch,
    contractorsRetry,
  ]);

  // Prefer fresh lookup labels without dropping the saved selection if not found.
  const selectedContractor = contractor
    ? contractors.find((item) => String(item.id) === String(contractor.id)) ||
      contractor
    : null;
  const contractorOptions = selectedContractor
    ? [
        selectedContractor,
        ...contractors.filter(
          (item) => String(item.id) !== String(selectedContractor.id),
        ),
      ]
    : contractors;

  const handleSave = async () => {
    if (saving) return;

    const payload = {
      name: name.trim(),
      subject: subject.trim(),
      body,
      scope,
      contractorId:
        scope === TEMPLATE_SCOPES.CONTRACTOR
          ? String(contractor?.id ?? "").trim() || null
          : null,
    };

    if (!payload.name) return setError("Template name is required.");
    if (!payload.subject) return setError("Subject is required.");
    if (isBodyEmpty(payload.body)) return setError("Body is required.");
    if (
      scope !== TEMPLATE_SCOPES.GLOBAL &&
      scope !== TEMPLATE_SCOPES.CONTRACTOR
    ) {
      return setError("Choose a valid template scope.");
    }
    if (scope === TEMPLATE_SCOPES.CONTRACTOR && !payload.contractorId) {
      return setError(
        "Select a contractor for this contractor-specific template.",
      );
    }

    const unknown = findUnknownPlaceholders(payload.subject, payload.body);
    if (unknown.length) {
      return setError(
        `Unknown placeholders: ${unknown.map(toToken).join(", ")}`,
      );
    }

    try {
      setSaving(true);
      setError("");
      const saved = isEdit
        ? await updateTemplate(currentSession, template.id, payload)
        : await createTemplate(currentSession, payload);
      onSaved(saved);
    } catch (e) {
      setError(getApiError(e, "Failed to save template."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>{isEdit ? "Edit template" : "New template"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Template name"
            required
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            select
            label="Template scope"
            required
            fullWidth
            value={scope}
            disabled={saving}
            onChange={(e) => setScope(e.target.value)}
            helperText={
              scope === TEMPLATE_SCOPES.CONTRACTOR
                ? "Available only for packages belonging to the selected contractor. All admins can manage it."
                : "Available for any contractor, including emails sent without a package."
            }
          >
            <MenuItem value={TEMPLATE_SCOPES.GLOBAL}>Global</MenuItem>
            <MenuItem value={TEMPLATE_SCOPES.CONTRACTOR}>
              Contractor-specific
            </MenuItem>
          </TextField>
          {scope === TEMPLATE_SCOPES.CONTRACTOR && (
            <>
              {contractorsError && (
                <Alert
                  severity="warning"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      disabled={saving || contractorsLoading}
                      onClick={() => setContractorsRetry((value) => value + 1)}
                    >
                      Retry
                    </Button>
                  }
                >
                  {contractorsError} An existing contractor selection is
                  preserved.
                </Alert>
              )}
              <Autocomplete
                fullWidth
                options={contractorOptions}
                value={selectedContractor}
                loading={contractorsLoading}
                disabled={saving}
                filterOptions={(options) => options}
                getOptionLabel={getContractorLabel}
                isOptionEqualToValue={(option, value) =>
                  String(option.id) === String(value.id)
                }
                onChange={(_, value) => setContractor(value)}
                onInputChange={(_, value, reason) => {
                  if (reason === "input" || reason === "clear") {
                    setContractorSearch(value);
                  }
                }}
                noOptionsText={
                  contractorsError
                    ? "Contractors could not be loaded"
                    : "No contractors found"
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Contractor"
                    required
                    helperText="Search by contractor or company name. Select one contractor."
                  />
                )}
              />
            </>
          )}
          <TextField
            label="Subject"
            required
            fullWidth
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            inputRef={subjectRef}
          />
          <RichTextEditor
            value={body}
            onChange={setBody}
            onEditorReady={handleEditorReady}
            minHeight={220}
          />
          <PlaceholderBar onInsert={insertPlaceholder} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          loading={saving}
          onClick={handleSave}
        >
          Save template
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateEditorDialog;
